import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Radio,
  Form,
  Spin,
} from "antd";
import { LoadingOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { format } from "date-fns";
import { BASE_URL } from "../../components/Common/AppConfig";
import SetAlert from "../../components/Common/SetAlert";

const emptyForm = {
  fullName: "",
  phone: "",
  email: "",
  dob: "",
  invitation: "",
  rakhi: "",
  patrika: "",
  location: "",
  houseNo: "",
  streetName: "",
  area: "",
  subuarb: "",
  cityMaster: "",
  stateMaster: "",
  countryMaster: "",
  pincode: "",
};

function AddressForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const history = useHistory();

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ success: "", error: "" });

  useEffect(() => {
    if (!isEdit) return;
    const fetchAddress = async () => {
      try {
        const response = await fetch(`${BASE_URL}/address/${id}`);
        const data = await response.json();
        if (response.ok) {
          const record = data.data || {};
          const parsedDob = record.dob ? new Date(record.dob) : null;
          setFormData({
            ...emptyForm,
            ...record,
            dob: parsedDob && !isNaN(parsedDob.getTime()) ? parsedDob : "",
          });
        } else {
          setAlertMessage({ success: "", error: "Failed to load address." });
        }
      } catch (error) {
        console.error(error);
        setAlertMessage({ success: "", error: "Failed to load address." });
      } finally {
        setLoading(false);
      }
    };
    fetchAddress();
  }, [id, isEdit]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const lookupPincode = async (pincode) => {
    const trimmed = (pincode || "").trim();
    if (!trimmed) return;
    // Indian pincodes are often stored with a space in the middle
    // (e.g. "380 001") - strip all whitespace before matching/looking up.
    const cleaned = trimmed.replace(/\s+/g, "");
    setPincodeLookupLoading(true);
    try {
      if (/^\d{6}$/.test(cleaned)) {
        try {
          const res = await fetch(
            `https://api.postalpincode.in/pincode/${cleaned}`,
          );
          const data = await res.json();
          const po =
            data?.[0]?.Status === "Success" ? data[0].PostOffice?.[0] : null;
          if (po) {
            setFormData((prev) => ({
              ...prev,
              cityMaster: po.District || prev.cityMaster,
              stateMaster: po.State || prev.stateMaster,
              countryMaster: po.Country || "India",
            }));
            return;
          }
        } catch (error) {
          console.error("India Post pincode lookup failed:", error);
        }
      }

      // Not a 6-digit Indian PIN (or lookup found nothing) - best-effort
      // guess at other countries via Zippopotam.us, since a bare postal
      // code alone doesn't tell us which country it belongs to.
      const guesses = /^\d{5}$/.test(cleaned) ? ["us"] : ["gb", "ca", "au"];
      for (const cc of guesses) {
        try {
          const res = await fetch(
            `https://api.zippopotam.us/${cc}/${encodeURIComponent(trimmed)}`,
          );
          if (!res.ok) continue;
          const data = await res.json();
          const place = data.places?.[0];
          if (place) {
            setFormData((prev) => ({
              ...prev,
              cityMaster: place["place name"] || prev.cityMaster,
              stateMaster:
                place["state"] || place["state abbreviation"] || prev.stateMaster,
              countryMaster: data.country || prev.countryMaster,
            }));
            return;
          }
        } catch (error) {
          // try the next country guess
        }
      }
    } finally {
      setPincodeLookupLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedDob = formData.dob
        ? [
            formData.dob.getDate(),
            formData.dob.getMonth() + 1,
            formData.dob.getFullYear(),
          ].join("-")
        : "";

      const requestBody = {
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        dob: formattedDob,
        invitation: formData.invitation,
        rakhi: formData.rakhi,
        patrika: formData.patrika,
        location: formData.location,
        houseNo: formData.houseNo,
        streetName: formData.streetName,
        area: formData.area,
        subuarb: formData.subuarb,
        cityMaster: formData.cityMaster,
        stateMaster: formData.stateMaster,
        countryMaster: formData.countryMaster,
        pincode: formData.pincode,
        createdDate: formData.createdDate,
      };

      const response = await fetch(
        isEdit ? `${BASE_URL}/address/${id}` : `${BASE_URL}/address`,
        {
          method: isEdit ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      if (response.ok) {
        history.push("/address", {
          alertMessage: {
            success: isEdit
              ? "Address edited successfully!"
              : "Address added successfully!",
            error: "",
          },
        });
      } else {
        setAlertMessage({
          success: "",
          error: `An error occurred while ${isEdit ? "editing" : "adding"} the address.`,
        });
      }
    } catch (error) {
      console.log(error);
      setAlertMessage({
        success: "",
        error: `An error occurred while ${isEdit ? "editing" : "adding"} the address.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    history.push("/address");
  };

  const formattedDob = formData.dob
    ? format(new Date(formData.dob), "dd-MM-yyyy")
    : "";

  if (loading) {
    return (
      <div className="content" style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="content">
      <Row>
        <Col md={24}>
          <Card
            title={
              <span>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleCancel}
                  style={{ marginRight: 8 }}
                />
                {isEdit ? "Edit Address" : "Add Address"}
              </span>
            }
          >
            <SetAlert
              alertMessage={alertMessage}
              setAlertMessage={setAlertMessage}
            />
            <Form layout="vertical" onFinish={handleSubmit}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Full Name" required>
                    <Input
                      name="fullName"
                      placeholder="Full Name"
                      value={formData.fullName || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Phone" required>
                    <Input
                      type="number"
                      name="phone"
                      placeholder="Phone"
                      value={formData.phone || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Email" required>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Date of Birth">
                    <Flatpickr
                      name="dob"
                      value={formattedDob || ""}
                      className="form-control d-block"
                      placeholder="Date of birth"
                      options={{
                        altInput: true,
                        altFormat: "F j, Y",
                        dateFormat: "d-m-Y",
                      }}
                      onChange={(date) =>
                        handleInputChange({
                          target: { name: "dob", value: date[0] },
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Invitation" required>
                    <Radio.Group
                      value={formData.invitation}
                      onChange={(e) =>
                        handleInputChange({
                          target: { name: "invitation", value: e.target.value },
                        })
                      }
                    >
                      <Radio value="Yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Rakhi" required>
                    <Radio.Group
                      value={formData.rakhi}
                      onChange={(e) =>
                        handleInputChange({
                          target: { name: "rakhi", value: e.target.value },
                        })
                      }
                    >
                      <Radio value="yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Patrika" required>
                    <Radio.Group
                      value={formData.patrika}
                      onChange={(e) =>
                        handleInputChange({
                          target: { name: "patrika", value: e.target.value },
                        })
                      }
                    >
                      <Radio value="Yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Location" required>
                    <Radio.Group
                      value={formData.location}
                      onChange={(e) =>
                        handleInputChange({
                          target: { name: "location", value: e.target.value },
                        })
                      }
                    >
                      <Radio value="yes">Abroad</Radio>
                      <Radio value="No">India</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="House Name" required>
                    <Input
                      name="houseNo"
                      placeholder="House Name, Plot No, Flat No"
                      value={formData.houseNo || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Street Name" required>
                    <Input
                      name="streetName"
                      placeholder="Street Name"
                      value={formData.streetName || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Area" required>
                    <Input
                      name="area"
                      placeholder="area"
                      value={formData.area || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Subuarb Name" required>
                    <Input
                      name="subuarb"
                      placeholder="Subuarb Name"
                      value={formData.subuarb || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Pincode" required>
                    <Input
                      name="pincode"
                      placeholder="pincode"
                      value={formData.pincode || ""}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (/^\d{6}$/.test(e.target.value.replace(/\s+/g, ""))) {
                          lookupPincode(e.target.value);
                        }
                      }}
                      onBlur={(e) => lookupPincode(e.target.value)}
                      suffix={
                        pincodeLookupLoading ? <LoadingOutlined spin /> : null
                      }
                      required
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="City" required>
                    <Input
                      name="cityMaster"
                      placeholder="Auto-filled from pincode"
                      value={formData.cityMaster || ""}
                      onChange={handleInputChange}
                      disabled
                      required
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="State" required>
                    <Input
                      name="stateMaster"
                      placeholder="Auto-filled from pincode"
                      value={formData.stateMaster || ""}
                      onChange={handleInputChange}
                      disabled
                      required
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Country Name" required>
                    <Input
                      name="countryMaster"
                      placeholder="Auto-filled from pincode"
                      value={formData.countryMaster || ""}
                      onChange={handleInputChange}
                      disabled
                      required
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
                <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  {submitting
                    ? isEdit
                      ? "Saving..."
                      : "Adding..."
                    : isEdit
                      ? "Save"
                      : "Add"}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AddressForm;
